/**
 * KRA Photo Journal – ShareLinkModal
 *
 * Staff-facing modal to generate a read-only public link for a customer to
 * view a single Drive folder gallery. Pick a TTL preset (or custom datetime),
 * copy the URL, view/revoke previously-issued links for the same record.
 *
 * Customer opens the link at `/?share=<token>` — no login required.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  createShareLink,
  listShareLinks,
  revokeShareLink,
  buildShareUrl,
  type ShareMeta,
} from "../lib/api.js";

interface Props {
  recordId: string;
  defaultTitle: string;
  onClose: () => void;
}

interface TTLPreset {
  label: string;
  seconds: number;
}

const PRESETS: TTLPreset[] = [
  { label: "1 giờ", seconds: 60 * 60 },
  { label: "2 giờ", seconds: 2 * 60 * 60 },
  { label: "6 giờ", seconds: 6 * 60 * 60 },
  { label: "24 giờ", seconds: 24 * 60 * 60 },
  { label: "7 ngày", seconds: 7 * 24 * 60 * 60 },
  { label: "30 ngày", seconds: 30 * 24 * 60 * 60 },
];

function formatRemaining(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Hết hạn";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `còn ${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `còn ${hours}h`;
  const days = Math.floor(hours / 24);
  return `còn ${days} ngày`;
}

export function ShareLinkModal({ recordId, defaultTitle, onClose }: Props) {
  const [presetSeconds, setPresetSeconds] = useState<number>(PRESETS[3]!.seconds); // 24h default
  const [customDateTime, setCustomDateTime] = useState<string>("");
  const [title, setTitle] = useState<string>(defaultTitle);
  const [allowDownload, setAllowDownload] = useState<boolean>(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [activeLinks, setActiveLinks] = useState<ShareMeta[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const refreshList = useCallback(() => {
    setLoadingList(true);
    listShareLinks(recordId)
      .then(setActiveLinks)
      .catch(() => setActiveLinks([]))
      .finally(() => setLoadingList(false));
  }, [recordId]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const ttlSeconds = useMemo(() => {
    if (customDateTime) {
      const target = new Date(customDateTime).getTime();
      const diff = Math.floor((target - Date.now()) / 1000);
      return diff > 0 ? diff : 0;
    }
    return presetSeconds;
  }, [customDateTime, presetSeconds]);

  const handleCreate = async () => {
    setError(null);
    if (ttlSeconds < 60) {
      setError("Thời hạn phải lớn hơn 1 phút");
      return;
    }
    setCreating(true);
    try {
      const res = await createShareLink({
        recordId,
        ttlSeconds,
        title: title.trim() || undefined,
        allowDownload,
      });
      setCreatedUrl(buildShareUrl(res.token));
      refreshList();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Tạo link thất bại";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleRevoke = async (token: string) => {
    if (!confirm("Thu hồi link này? Khách sẽ không xem được nữa.")) return;
    try {
      await revokeShareLink(token);
      refreshList();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Thu hồi thất bại";
      alert(msg);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="share-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[95] bg-black/75 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        key="share-content"
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.18 }}
        className="fixed inset-0 z-[96] flex items-start sm:items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <div
          className="w-full max-w-lg bg-[#15151c] border border-white/10 rounded-2xl shadow-2xl overflow-hidden my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-white/5">
            <div className="flex items-center justify-between">
              <h2 className="text-white text-lg font-medium">Chia sẻ bộ ảnh cho khách</h2>
              <button
                onClick={onClose}
                className="text-white/50 hover:text-white/80 transition w-8 h-8 flex items-center justify-center -mr-2"
              >
                ✕
              </button>
            </div>
            <p className="text-white/50 text-xs mt-1">
              Link công khai, chỉ xem không tải. Tự hết hạn theo thời gian đã đặt.
            </p>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-5">
            {/* Title */}
            <div>
              <label className="text-white/60 text-xs uppercase tracking-wide">Tên hiển thị</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="VD: Bộ ảnh kimono của chị Linh"
                className="mt-1.5 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-white/30"
                maxLength={200}
              />
            </div>

            {/* TTL preset */}
            <div>
              <label className="text-white/60 text-xs uppercase tracking-wide">Thời hạn</label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.seconds}
                    onClick={() => { setPresetSeconds(p.seconds); setCustomDateTime(""); }}
                    className={[
                      "px-3 py-2 text-sm rounded-lg border transition",
                      !customDateTime && presetSeconds === p.seconds
                        ? "bg-[#B76E6E]/20 border-[#B76E6E]/60 text-white"
                        : "bg-black/20 border-white/10 text-white/70 hover:border-white/30",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <details className="mt-2">
                <summary className="text-white/40 text-xs cursor-pointer hover:text-white/70">
                  Tuỳ chỉnh thời điểm hết hạn
                </summary>
                <input
                  type="datetime-local"
                  value={customDateTime}
                  onChange={(e) => setCustomDateTime(e.target.value)}
                  className="mt-2 w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                />
              </details>
            </div>

            {/* Download permission */}
            <div>
              <label className="text-white/60 text-xs uppercase tracking-wide">Quyền tải về</label>
              <button
                type="button"
                onClick={() => setAllowDownload((v) => !v)}
                className="mt-1.5 w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-black/20 border border-white/10 hover:border-white/30 rounded-lg transition text-left"
              >
                <div className="min-w-0">
                  <div className="text-white text-sm">
                    {allowDownload ? "Cho phép khách tải ảnh" : "Chỉ xem — không cho tải"}
                  </div>
                  <div className="text-white/40 text-[11px] mt-0.5">
                    {allowDownload
                      ? "Hiện nút tải về trên từng ảnh + cho phép chuột phải / lưu ảnh."
                      : "Ẩn nút tải, chặn chuột phải + kéo lưu ảnh (chỉ hạn chế social, không phải DRM)."}
                  </div>
                </div>
                {/* Toggle switch */}
                <div
                  className={[
                    "shrink-0 w-10 h-6 rounded-full transition-colors relative",
                    allowDownload ? "bg-[#B76E6E]" : "bg-white/15",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all",
                      allowDownload ? "left-[18px]" : "left-0.5",
                    ].join(" ")}
                  />
                </div>
              </button>
            </div>

            {/* Create button + result */}
            {!createdUrl && (
              <button
                onClick={handleCreate}
                disabled={creating || ttlSeconds < 60}
                className="w-full bg-[#B76E6E] hover:bg-[#a45e5e] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium py-2.5 rounded-lg transition"
              >
                {creating ? "Đang tạo…" : "Tạo link chia sẻ"}
              </button>
            )}

            {error && (
              <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            {createdUrl && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-3 space-y-2">
                <div className="text-emerald-300 text-xs">✓ Link đã sẵn sàng — gửi cho khách</div>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={createdUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1.5 text-white text-xs"
                  />
                  <button
                    onClick={handleCopy}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded transition"
                  >
                    {copied ? "Đã copy" : "Copy"}
                  </button>
                </div>
                <button
                  onClick={() => { setCreatedUrl(null); setCopied(false); }}
                  className="text-white/40 hover:text-white/70 text-xs"
                >
                  Tạo link khác
                </button>
              </div>
            )}

            {/* Active links list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/60 text-xs uppercase tracking-wide">Link đang hoạt động</div>
                {loadingList && <div className="text-white/30 text-xs">Đang tải…</div>}
              </div>
              {!loadingList && activeLinks.length === 0 && (
                <div className="text-white/30 text-xs italic">Chưa có link nào.</div>
              )}
              <div className="space-y-1.5">
                {activeLinks.map((link) => (
                  <div
                    key={link.token}
                    className="flex items-center justify-between bg-black/20 border border-white/5 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-xs truncate">{link.title}</div>
                      <div className="text-white/40 text-[10px]">
                        {formatRemaining(link.expiresAt)}
                        {link.allowDownload ? " · cho tải" : " · chỉ xem"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 ml-2 shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(buildShareUrl(link.token));
                          } catch {/* ignore */}
                        }}
                        className="text-white/50 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10 transition"
                        title="Copy URL"
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => handleRevoke(link.token)}
                        className="text-red-400/70 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-red-500/10 transition"
                        title="Thu hồi"
                      >
                        Thu hồi
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
